import { useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import { gsap } from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger';
import PageHeader from '../components/PageHeader';
import Section from '../components/Section';
import ServiceCard from '../components/ServiceCard';
import { updatePageMeta } from '../lib/seo';
import { serviceCategories, serviceBenefits } from '../data/services';
import img1 from "../assets/ourServices/img1.avif"
import img2 from "../assets/ourServices/img2.avif"
import img3 from "../assets/ourServices/img3.avif"

gsap.registerPlugin(ScrollTrigger);

function Services() {
  const sectionRefs = useRef([]);
  sectionRefs.current = [];

  const addToRefs = (el) => {
    if (el && !sectionRefs.current.includes(el)) sectionRefs.current.push(el);
  };

  useEffect(() => {
    updatePageMeta({
      title: 'Our Services - EnviroTech Solutions',
      description:
        'Comprehensive environmental testing, consultancy, and waste management services for industries, institutions, and government bodies.',
    });

    // Animate reveal-item elements
    const revealItems = document.querySelectorAll('.reveal-item');
    revealItems.forEach((item, index) => {
      gsap.fromTo(
        item,
        { opacity: 0, y: 20, scale: 0.98 },
        {
          opacity: 1,
          y: 0,
          scale: 1,
          duration: 0.8,
          delay: index * 0.05,
          ease: 'power2.out',
          scrollTrigger: {
            trigger: item,
            start: 'top 92%',
            toggleActions: 'play none none none',
          },
        }
      );
    });

    // Cleanup function
    return () => {
      ScrollTrigger.getAll().forEach(trigger => trigger.kill());
    };
  }, []);

  const breadcrumbs = [
    { label: 'Home', href: '/' },
    { label: 'Services' },
  ];

  // 🌿 Add default fallback images for services
  const defaultImages = {
    testing:
    img1,
   
      consultancy:img2,
    waste: img3,  
     // 'https://images.unsplash.com/photo-1622322977885-8ee6d45ea016?ixlib=rb-4.1.0&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D&auto=format&fit=crop&q=80&w=1074', // waste management
    air:
      'https://images.unsplash.com/photo-1503264116251-35a269479413?auto=format&fit=crop&w=800&q=80', // air quality
    water:
      'https://images.unsplash.com/photo-1505751172876-fa1923c5c528?auto=format&fit=crop&w=800&q=80', // water testing
  };

  return (
    <>
      <PageHeader
        title="Our Services"
        description="Comprehensive environmental solutions backed by technical expertise and ISO/NABL accreditation"
        breadcrumbs={breadcrumbs}
        image="https://images.unsplash.com/photo-1599058917214-7a38d8d8c7f7?auto=format&fit=crop&w=1600&q=80"
      />

      {/* Service Categories */}
      <Section ref={addToRefs}>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 md:gap-8">
          {serviceCategories.map((category) => (
            <div key={category.id} className="reveal-item flex">
              <ServiceCard
                title={category.title}
                description={category.description}
                href={category.href}
                features={category.services.slice(0, 5).map((s) => s.name)}
                image={
                  category.id === 'testing'
                    ? defaultImages.testing
                    : category.id === 'consultancy'
                    ? defaultImages.consultancy
                    : category.id === 'waste'
                    ? defaultImages.waste
                    : defaultImages.air
                }
              />
            </div>
          ))}
        </div>
      </Section>

      {/* Service Benefits */}
      <Section
        ref={addToRefs}
        title="Why Choose Our Services"
        subtitle="Quality, expertise, and commitment to your environmental needs"
        background="gray"
      >
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 md:gap-8">
          {serviceBenefits.map((benefit, index) => (
            <div key={index} className="card p-6 reveal-item hover:shadow-xl transition-all duration-300 border border-accent-100 hover:border-primary-300">
              <div className="w-12 h-12 rounded-lg bg-primary-100 flex items-center justify-center text-2xl mb-4 shadow-sm">
                {benefit.icon}
              </div>
              <h3 className="text-lg font-bold text-accent-900 mb-2">
                {benefit.title}
              </h3>
              <p className="text-accent-600 leading-relaxed">{benefit.description}</p>
            </div>
          ))}
        </div>
      </Section>

      {/* Process Section */}
      <Section ref={addToRefs} title="Our Service Process">
        <div className="max-w-6xl mx-auto">
          <div className="relative">
            {/* Connection line - hidden on mobile, visible on desktop */}
            <div className="hidden lg:block absolute top-8 left-0 right-0 h-0.5 bg-gradient-to-r from-primary-200 via-primary-400 to-secondary-200 z-0"></div>
            
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 lg:gap-8 relative z-10">
              {[
                {
                  step: '01',
                  title: 'Consultation',
                  description:
                    'Understand your requirements and recommend appropriate testing or services.',
                },
                {
                  step: '02',
                  title: 'Sampling',
                  description:
                    'Collect samples using proper protocols or provide onsite testing.',
                },
                {
                  step: '03',
                  title: 'Analysis',
                  description:
                    'Conduct testing using validated methods in our accredited laboratory.',
                },
                {
                  step: '04',
                  title: 'Reporting',
                  description:
                    'Deliver comprehensive reports with interpretations and recommendations.',
                },
              ].map((phase, index) => (
                <div key={index} className="reveal-item relative">
                  <div className="bg-white rounded-lg p-6 md:p-8 shadow-sm hover:shadow-md transition-all duration-300 border border-accent-200 hover:border-primary-300 h-full flex flex-col items-center text-center">
                    {/* Step number with gradient */}
                    <div className="relative mb-5">
                      <div className="w-16 h-16 bg-gradient-to-br from-primary-600 to-secondary-600 rounded-full flex items-center justify-center mx-auto shadow-lg">
                        <span className="text-xl md:text-2xl font-bold text-white">
                          {phase.step}
                        </span>
                      </div>
                      {/* Decorative ring */}
                      <div className="absolute inset-0 w-16 h-16 rounded-full border-2 border-primary-200 opacity-50 animate-pulse"></div>
                    </div>
                    
                    {/* Title */}
                    <h3 className="text-base md:text-lg font-semibold text-accent-900 mb-3 leading-tight">
                      {phase.title}
                    </h3>
                    
                    {/* Description */}
                    <p className="text-sm text-accent-600 leading-relaxed flex-grow">
                      {phase.description}
                    </p>
                    
                    {/* Arrow connector for mobile/tablet */}
                    {index < 3 && (
                      <>
                        <div className="lg:hidden absolute -bottom-3 left-1/2 transform -translate-x-1/2 w-8 h-8 flex items-center justify-center">
                          <svg className="w-5 h-5 text-primary-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 9l-7 7-7-7" />
                          </svg>
                        </div>
                      </>
                    )}
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </Section>

      {/* Industries Served */}
      <Section
        ref={addToRefs}
        title="Industries We Serve"
        subtitle="Tailored solutions for diverse sectors"
        background="pattern"
      >
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4 md:gap-6">
          {[
            { name: 'Manufacturing', icon: '🏭' },
            { name: 'Healthcare', icon: '🏥' },
            { name: 'Construction', icon: '🏗️' },
            { name: 'Hospitality', icon: '🏨' },
            { name: 'Education', icon: '🎓' },
            { name: 'Government', icon: '🏛️' },
            { name: 'Food & Beverage', icon: '🍽️' },
            { name: 'Pharmaceuticals', icon: '💊' },
          ].map((industry, index) => (
            <div
              key={index}
              className="reveal-item card p-5 md:p-6 text-center hover:shadow-xl transition-all duration-300 bg-white/80 hover:-translate-y-1 border border-accent-200 hover:border-primary-300"
            >
              <div className="text-3xl md:text-4xl mb-3">{industry.icon}</div>
              <h3 className="text-sm md:text-base font-semibold text-accent-900">
                {industry.name}
              </h3>
            </div>
          ))}
        </div>
      </Section>

      {/* CTA */}
      <Section ref={addToRefs}>
        <div className="bg-gradient-to-br from-primary-600 to-secondary-600 rounded-xl p-8 md:p-12 text-center text-white shadow-lg">
          <h2 className="text-2xl md:text-3xl font-bold mb-4">
            Need a Custom Solution?
          </h2>
          <p className="text-base md:text-xl mb-8 text-primary-100 max-w-2xl mx-auto leading-relaxed">
            We understand that every project has unique requirements. Contact
            us to discuss your specific environmental needs and we'll develop a
            tailored solution.
          </p>
          <Link
            to="/enquiry"
            className="btn bg-white text-primary-600 hover:bg-primary-50"
          >
            Request Custom Quote
          </Link>
        </div>
      </Section>
    </>
  );
}

export default Services;
